import { useCallback, useEffect, useMemo } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import {
  DayOfMonth,
  DayOfMonthSelector,
} from '@/modules/tasks/components/task-scheduler-dialog/day-of-month-selector.tsx'
import type { Month } from '@/modules/tasks/components/task-scheduler-dialog/month-selector.tsx'
import {
  MonthSelector,
  monthKeys,
  months,
} from '@/modules/tasks/components/task-scheduler-dialog/month-selector.tsx'

export const YearlyRepetitionSelector = () => {
  const { control } = useFormContext()
  const month = useController({
    control,
    name: 'rateMonth',
  })
  const day = useController({
    control,
    name: 'rateDayOfMonth',
  })

  const monthKey = useMemo(() => {
    const value = month.field.value ?? 0

    if (value <= 0) {
      return null
    }

    return monthKeys[value - 1]
  }, [month.field.value])

  const dayCount = useMemo(
    () => (monthKey == null ? 31 : months[monthKey]),
    [monthKey],
  )

  useEffect(() => {
    if (day.field.value > dayCount) {
      day.field.onChange(dayCount)
    }
  }, [day.field, dayCount])

  const handleChangeMonth = useCallback(
    (key: Month) => {
      const monthIndex = monthKeys.indexOf(key)

      if (monthIndex >= 0) {
        month.field.onChange(monthIndex + 1)
      }
    },
    [month.field],
  )

  return (
    <>
      <div className="flex items-center gap-2">
        <DayOfMonthSelector
          value={day.field.value}
          onChange={day.field.onChange}
          dayCount={dayCount}
        />
        <span className="text-sm">of</span>
        <MonthSelector
          value={monthKey}
          onChange={handleChangeMonth}
        />
      </div>

      <span className="block text-secondary-300 text-xs">
        <YearlyScheduleNote
          day={day.field.value}
          month={monthKey}
        />
      </span>
    </>
  )
}

interface YearlyScheduleNoteProps {
  day: number
  month: Month | null
}

const YearlyScheduleNote = (props: YearlyScheduleNoteProps) => {
  const { day, month } = props

  if (!day) {
    return 'No day is selected'
  }
  if (!month) {
    return 'No month is selected'
  }
  if (day && month) {
    return (
      <>
        The Task will be scheduled on the{' '}
        <strong className="font-medium">
          <DayOfMonth day={day} />
        </strong>{' '}
        day of <strong className="font-medium">{month}</strong>
      </>
    )
  }
}
